/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Fragment } from 'react';
import { writeFile, utils } from 'xlsx';
import PropTypes from 'prop-types';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { ClipboardIcon, ArrowDownOnSquareIcon, ScissorsIcon } from '@heroicons/react/24/outline';
import { ArrowDownOnSquareIcon as SaveSolidIcon } from '@heroicons/react/24/solid';
import { TableRowInsertAfter, TableRowInsertBefore, TableColumnInsertAfter, TableColumnInsertBefore, DuplicateRow, DuplicateColumn, DeleteRow, DeleteColumn } from '../../icons/remoteAssets';

import '../../css/jspreadsheet.css';

function DuplicateIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

// this is compatible with AccountDetails 2.0
// this is supposed to only do context menu stuff; there shall be no category-specific logic here
export default function ContextMenu({ obj, jRef, contextMenu, userChangesPresent, setUserChangesPresent, gridLayout, accountName, enableAddDeleteColumns, setTransformationParams }) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'contextMenu' });

  function calculateCsvSeparator() {
    const list = ['a', 'b'];
    const s = list.toLocaleString();
    const sep = s[1];
    return sep;
  }

  // adds the command to transformationParams.commands
  // this function is duplicated in Grid
  function addCommandToHistory(command, args) {
    setTransformationParams((prev) => {
      const newCommands = [...prev.commands, { command, args }];
      return { ...prev, commands: newCommands };
    });
  }

  function handleInsertRows(e, before) {
    // if there is more than one row, insert all rows before the first or after the last selected row, depending on the value of before
    const sel = obj.getSelectedRows(true);
    for (let i = sel.length; i > 0; i -= 1) {
      if (obj) obj.insertRow(1, before ? Math.min(...sel) : Math.max(...sel), before);
    }
  }

  function handleInsertColumns(e, before) {
    // if there is more than one column, insert all columns before the first or after the last selected column, depending on the value of before
    const sel = obj.getSelectedColumns(true); // returns the indices of the selected columns in an array
    if (obj) {
      obj.insertColumn(sel.length, before ? Math.min(...sel) : Math.max(...sel), before, { type: 'text' });
      addCommandToHistory('insertColumn', [sel.length, before ? Math.min(...sel) : Math.max(...sel), before]);
    }
  }

  function handleDeleteRows(e) {
    // for each currently selected row issue a deleteRow command
    const sel = obj.getSelectedRows(true);
    for (let i = sel.length; i > 0; i -= 1) {
      jRef.current.jexcel.deleteRow(sel[i - 1], 1);
      addCommandToHistory('deleteRow', [sel[i - 1]]);
    }
    if (!userChangesPresent) setUserChangesPresent(true);
  }

  function handleDeleteColumn(e) {
    // for each currently selected column issue a deleteColumn command
    const sel = obj.getSelectedColumns(true);
    console.log('DEBUG context menu > handleDelCol', e);
    for (let i = sel.length; i > 0; i -= 1) {
      jRef.current.jexcel.deleteColumn(sel[i - 1], 1);
      addCommandToHistory('deleteColumn', [sel[i - 1]]);
    }
    if (!userChangesPresent) setUserChangesPresent(true);
  }

  function handlePasteViaMenu(e) {
    // when paste command is triggered via context menu
    if (navigator && navigator.clipboard) {
      // if browser has the clipboard API
      const x = Math.min(...jRef.current.jexcel.getSelectedColumns(true)); // get top left corner of selection
      const y = Math.min(...jRef.current.jexcel.getSelectedRows(true));
      if (obj.selectedCell) {
        navigator.clipboard.readText().then((text) => {
          if (text) {
            jRef.current.jexcel.paste(x, y, text);
          }
        });
      }
    }
  }

  function handleDuplicateRows(e) {
    // copy selected rows
    jRef.current.jexcel.copy(true); // true means copy the selection only
    // insert selected rows
    handleInsertRows(e, true, obj);
    // paste selected rows
    handlePasteViaMenu(e);
  }

  function handleDuplicateColumns(e) {
    // copy selected columns
    jRef.current.jexcel.copy(true); // true means copy the selection only
    // insert selected columns
    handleInsertColumns(e, true, obj);
    // paste selected rows
    handlePasteViaMenu(e);
  }

  function handleExport(e, selectionOnly = false) {
    let data;
    if (!gridLayout) throw new Error('GridContextMenu: gridLayout is not defined');

    if (selectionOnly) {
      data = jRef.current.jexcel.getData(true); // hidden columns are not included when only getting selection
    } else {
      data = jRef.current.jexcel
        .getData() // hidden columns are included when getting all data, so we need to filter them out
        // remove id column (based on gridLayout)
        .map((row) => row.filter((_, i) => gridLayout[i].id !== 'id'));
    }
    const xlsxObj = utils.aoa_to_sheet(data);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, xlsxObj, 'Sheet1');
    writeFile(workbook, `${accountName} export.xlsx`, { bookType: 'xlsx', type: 'binary' });
  }

  const menuItemFormat = 'hover:bg-gray-100 hover:text-gray-900 text-gray-700 group flex w-full items-center text-left px-4 py-2 text-sm';
  const iconFormat = 'mr-4 h-4 w-4 text-gray-400 group-hover:text-gray-500';

  return (
    <Transition
      show={contextMenu.visible}
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <div
        // eslint-disable-next-line react/style-prop-object
        style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
        onMouseDown={(e) => {
          e.preventDefault(); // the default behaviour of jspreadsheet was that this reset the selection within spreadsheet on mousedown
          e.stopPropagation(); // (before the copy command could have been executed)
        }}
        className="origin-top-right fixed z-50 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none"
      >
        <div className="py-1">
          <div>
            <button type="button" onClick={(e) => handleInsertRows(e, true)} className={menuItemFormat}>
              <TableRowInsertBefore className={iconFormat} aria-hidden="true" />
              {t('addEmptyRowAbove')}
            </button>
          </div>
          <div>
            <button type="button" onClick={(e) => handleInsertRows(e, false)} className={menuItemFormat}>
              <TableRowInsertAfter className={iconFormat} aria-hidden="true" />
              {t('addEmptyRowBelow')}
            </button>
          </div>
          {enableAddDeleteColumns && (
            <>
              <div>
                <button type="button" onClick={(e) => handleInsertColumns(e, false)} className={menuItemFormat}>
                  <TableColumnInsertBefore className={iconFormat} aria-hidden="true" />
                  {t('addEmptyColumnAbove')}
                </button>
              </div>
              <div>
                <button type="button" onClick={(e) => handleInsertColumns(e, true)} className={menuItemFormat}>
                  <TableColumnInsertAfter className={iconFormat} aria-hidden="true" />
                  {t('addEmptyColumnBelow')}
                </button>
              </div>
            </>
          )}
        </div>
        <div className="py-1">
          <div>
            <button type="button" onClick={handleDuplicateRows} className={menuItemFormat}>
              <DuplicateRow className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
              {t('duplicateRow')}
            </button>
          </div>
          {enableAddDeleteColumns && (
            <div>
              <button type="button" onClick={handleDuplicateColumns} className={menuItemFormat}>
                <DuplicateColumn className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                {t('duplicateColumn')}
              </button>
            </div>
          )}
          <div>
            <button type="button" id="deleteRow" onClick={handleDeleteRows} className={menuItemFormat}>
              <DeleteRow className="mr-4 h-4 w-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
              {t('deleteRow')}
            </button>
          </div>
          {enableAddDeleteColumns && (
            <div>
              <button type="button" id="deleteColumn" onClick={handleDeleteColumn} className={menuItemFormat}>
                <DeleteColumn className="mr-4 h-4 w-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                {t('deleteColumn')}
              </button>
            </div>
          )}
        </div>
        <div className="py-1">
          <div>
            <button
              type="button"
              onClick={(e) => {
                jRef.current.jexcel.copy(true); // true means copy the selection only
                jRef.current.jexcel.getSelectedColumns(true).forEach((col) => {
                  jRef.current.jexcel.getSelectedRows(true).forEach((row) => {
                    jRef.current.jexcel.setValueFromCoords(col, row, '');
                  });
                });
                jRef.current.jexcel.removeCopyingSelection();
              }}
              className={menuItemFormat}
            >
              <ScissorsIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
              {t('cut')}
            </button>
          </div>
          <div>
            <button
              type="button"
              onClick={(e) => {
                jRef.current.jexcel.copy(true); // true means copy the selection only
                jRef.current.jexcel.removeCopyingSelection();
              }}
              className={menuItemFormat}
            >
              <DuplicateIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
              {t('copy')}
            </button>
          </div>
          <div>
            <button onClick={handlePasteViaMenu} type="button" className={menuItemFormat}>
              <ClipboardIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
              {t('paste')}
            </button>
          </div>
        </div>
        <div className="py-1">
          <div>
            <button
              type="button"
              onClick={(e) => {
                handleExport(e, false);
              }}
              className={menuItemFormat}
            >
              <ArrowDownOnSquareIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
              {t('download')}
            </button>
          </div>
          <button
            type="button"
            onClick={(e) => {
              handleExport(e, true);
            }}
            className={menuItemFormat}
          >
            <SaveSolidIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
            {t('downloadSelected')}
          </button>
        </div>
      </div>
    </Transition>
  );
}
ContextMenu.propTypes = {
  contextMenu: PropTypes.objectOf(PropTypes.any).isRequired,
  obj: PropTypes.objectOf(PropTypes.any), // when component initialised, obj is not yet present
  jRef: PropTypes.objectOf(PropTypes.any).isRequired,
  userChangesPresent: PropTypes.bool.isRequired,
  setUserChangesPresent: PropTypes.func.isRequired,
  gridLayout: PropTypes.array,
  accountName: PropTypes.string.isRequired,
  enableAddDeleteColumns: PropTypes.bool,
  setTransformationParams: PropTypes.func,
};
ContextMenu.defaultProps = {
  obj: {},
  gridLayout: [],
  enableAddDeleteColumns: false,
  setTransformationParams: () => {},
};
